import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { useAuthorization } from '../../auth/useAuthorization';
import { Role } from '../../auth/roles';
import { deleteNotification, markAllNotificationsRead } from '../../utils/notificationApi';

/**
 * Reusable Notification component that fetches and displays a scrolling message from the backend.
 * Tenant users can mark all notifications as read. System Admin can clear the latest one.
 */
const NotificationBanner: React.FC<{ commonOnly?: boolean }> = ({ commonOnly = false }) => {
  const [message, setMessage] = useState<string>('');
  const [notificationId, setNotificationId] = useState<number | null>(null);
  const [markingRead, setMarkingRead] = useState(false);
  const [clearing, setClearing] = useState(false);
  const { isAuthenticated, hasRole } = useAuthorization();
  const isSystemAdmin = hasRole(Role.ADMIN);
  const showMarkAllRead = isAuthenticated && !isSystemAdmin && !commonOnly;
  const showClearLatest = isAuthenticated && isSystemAdmin && !commonOnly && notificationId != null;

  const fetchNotification = useCallback(async () => {
    try {
      const response = await apiService.get(
        commonOnly ? `${apiRoutes.notification}?commonOnly=true` : apiRoutes.notification,
      );
      const res = response as any;
      const payload = res?.data !== undefined ? res.data : res;
      let text = '';
      let record: Record<string, unknown> | null = null;
      if (typeof payload === 'string') {
        text = payload;
      } else if (payload && typeof payload === 'object') {
        if (Array.isArray(payload) && payload.length > 0) {
          const latest = [...payload].sort((a, b) => {
            const aRow = a as Record<string, unknown>;
            const bRow = b as Record<string, unknown>;
            const aTime = new Date(String(aRow.createdAt ?? aRow.CreatedAt ?? 0)).getTime();
            const bTime = new Date(String(bRow.createdAt ?? bRow.CreatedAt ?? 0)).getTime();
            return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
          })[0] as Record<string, unknown>;
          record = latest;
          const title = String(latest?.title ?? latest?.Title ?? '').trim();
          const body = String(latest?.message ?? latest?.Message ?? latest?.text ?? latest?.content ?? '').trim();
          text = [title, body].filter(Boolean).join(' — ');
        } else {
          record = payload as Record<string, unknown>;
          text =
            (payload as any).message ??
            (payload as any).Message ??
            (payload as any).text ??
            (payload as any).content ??
            (payload as any).value ??
            (payload as any).notification ??
            (payload as any).data ??
            (payload as any).result ??
            '';
        }
      }

      const pickDate = (obj: Record<string, unknown> | null, ...keys: string[]) => {
        if (!obj) return null;
        for (const k of keys) {
          const v = obj[k] ?? obj[k.charAt(0).toUpperCase() + k.slice(1)];
          if (v != null && v !== '') return String(v);
        }
        return null;
      };

      const fromRaw = pickDate(record, 'displayFrom', 'validFrom', 'startDateTime', 'from');
      const toRaw = pickDate(record, 'displayTo', 'validTo', 'endDateTime', 'to');
      const now = Date.now();
      if (fromRaw) {
        const t = new Date(fromRaw).getTime();
        if (!Number.isNaN(t) && now < t) {
          setMessage('');
          setNotificationId(null);
          return;
        }
      }
      if (toRaw) {
        const t = new Date(toRaw).getTime();
        if (!Number.isNaN(t) && now > t) {
          setMessage('');
          setNotificationId(null);
          return;
        }
      }

      const trimmed = String(text ?? '').trim();
      const rawId = record?.id ?? record?.Id;
      const parsedId = rawId == null || rawId === '' ? NaN : Number(rawId);
      setMessage(trimmed);
      setNotificationId(trimmed && Number.isFinite(parsedId) ? parsedId : null);
    } catch {
      setMessage('');
      setNotificationId(null);
    }
  }, [commonOnly]);

  useEffect(() => {
    void fetchNotification();
  }, [fetchNotification]);

  useEffect(() => {
    const onCleared = () => {
      setMessage('');
      setNotificationId(null);
    };
    window.addEventListener('notifications-cleared', onCleared);
    return () => window.removeEventListener('notifications-cleared', onCleared);
  }, []);

  const handleMarkAllRead = async () => {
    setMarkingRead(true);
    try {
      const result = await markAllNotificationsRead();
      setMessage('');
      setNotificationId(null);
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: result.message ?? 'All notifications marked as read.',
            type: 'success',
          },
        }),
      );
    } catch (err: unknown) {
      const msg =
        (err as { message?: string; error?: string })?.message ??
        (err as { error?: string })?.error ??
        'Failed to mark notifications as read';
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: String(msg), type: 'error' },
        }),
      );
    } finally {
      setMarkingRead(false);
    }
  };

  const handleClearLatest = async () => {
    if (notificationId == null) return;
    setClearing(true);
    try {
      await deleteNotification(notificationId);
      setMessage('');
      setNotificationId(null);
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: 'Latest notification cleared.', type: 'success' },
        }),
      );
      await fetchNotification();
    } catch (err: unknown) {
      const msg =
        (err as { message?: string; error?: string })?.message ??
        (err as { error?: string })?.error ??
        'Failed to clear the latest notification';
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: String(msg), type: 'error' },
        }),
      );
    } finally {
      setClearing(false);
    }
  };

  if (!message && !showMarkAllRead) {
    return null;
  }

  if (!message) {
    return null;
  }

  return (
    <div className="w-full overflow-hidden border-0 bg-blue-900/80 py-1.5 shadow-none dark:bg-slate-800">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="animate-marquee whitespace-nowrap text-sm text-blue-100 dark:text-slate-100">
            <span className="inline-block pr-8">{message}</span>
            <span className="inline-block pr-8" aria-hidden="true">{message}</span>
          </div>
        </div>
        {showClearLatest && (
          <button
            type="button"
            onClick={() => void handleClearLatest()}
            disabled={clearing}
            className="shrink-0 mr-2 rounded bg-blue-700/80 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-blue-600 disabled:opacity-60 dark:bg-slate-600 dark:hover:bg-slate-500"
            title="Remove the notification that is scrolling"
          >
            {clearing ? '…' : 'Clear'}
          </button>
        )}
        {/* {showMarkAllRead && (
          <button
            type="button"
            onClick={() => void handleMarkAllRead()}
            disabled={markingRead}
            className="shrink-0 mr-2 rounded bg-blue-700/80 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-blue-600 disabled:opacity-60 dark:bg-slate-600 dark:hover:bg-slate-500"
            title="Mark all notifications as read"
          >
            {markingRead ? '…' : 'Mark all read'}
          </button>
        )} */}
      </div>
    </div>
  );
};

export default NotificationBanner;
